import { useMemo } from 'react';

import { useAppSelector } from '@/hooks/redux';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectAdobeSignFormFeature } from '@/redux/configs/selectors';

import { EmploymentStatus } from '@/types/user';

export const useCheckPersonalDetails = () => {
  const user = useAppSelector(selectLoggedInUser);
  const isAdobeSignFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const isEnhancedTaxSectionVisible = !isAdobeSignFormEnabled;

  const isEmploymentInfoComplete = useMemo((): boolean => {
    if (!user) {
      return false;
    }

    const { employmentDetailed } = user;

    if (!employmentDetailed?.employmentStatus) {
      return false;
    }

    if (employmentDetailed.employmentStatus === EmploymentStatus.Employed) {
      return Boolean(
        employmentDetailed.employerName?.trim() &&
          employmentDetailed.occupationCode?.trim(),
      );
    }

    return true;
  }, [user]);

  /**
   * Existing customer requires some field to consider as complete
   * if all required fields are filled, then the user info is complete and show Continue button
   */
  const isUserInfoComplete = useMemo((): boolean => {
    if (!user) {
      return false;
    }

    // First of all for employment information, EXC must have employment status
    const employmentRequiredFields: Array<string | boolean | undefined> = [
      user.employmentDetailed?.employmentStatus,
    ];

    // #region Base on each type of employment status, we have different required fields
    if (
      user.employmentDetailed?.employmentStatus === EmploymentStatus.Employed
    ) {
      employmentRequiredFields.push(
        user.employmentDetailed.employerName,
        user.employmentDetailed.occupationCode,
        user.employmentDetailed.employerPhone,
        user.employmentDetailed.employerAddress,
      );
    }

    if (
      user.employmentDetailed?.employmentStatus ===
      EmploymentStatus.SelfEmployed
    ) {
      employmentRequiredFields.push(user.employmentDetailed.riskSectorFlag);
      if (user.employmentDetailed.riskSectorFlag === false) {
        employmentRequiredFields.push(user.employmentDetailed.employmentDesc);
      }
    }

    if (
      user.employmentDetailed?.employmentStatus === EmploymentStatus.Unemployed
    ) {
      employmentRequiredFields.push(user.employmentDetailed.employmentDesc);
    }
    // #endregion

    const defaultRequiredFields = [
      user.citizenship,
      user.phoneNumber,
      user?.addressDetailed?.line1, // line1 is Address/Street on UI
      user?.addressDetailed?.country,
      ...(isEnhancedTaxSectionVisible ? [] : [user.taxResidences]),
    ];

    const isCountrySameTenant =
      user.territoryCode === user?.addressDetailed?.country;
    if (isCountrySameTenant) {
      defaultRequiredFields.push(user?.addressDetailed?.city);
    }

    // Other required fields relate to below address and contact information
    const requiredFields = [
      ...defaultRequiredFields,
      ...employmentRequiredFields,
    ];

    // Check if all required fields are filled and none of them is undefined/empty
    const hasAllBasicFields = requiredFields.every(
      val => val !== undefined && val !== '' && val !== null,
    );
    const hasCompleteEmploymentInfo = isEmploymentInfoComplete;

    return hasAllBasicFields && hasCompleteEmploymentInfo;
  }, [isEmploymentInfoComplete, user, isEnhancedTaxSectionVisible]);

  return {
    isUserInfoComplete,
    user,
  };
};
